<template>
  <div class="p-file-text-input">
    <p-text-input
      v-model="chatInput.value"
      name="chatInput"
      :label="$t(label)"
      :disabled="loading || disabled"
      :placeholder="filePlaceholder"
      @keydown.enter.prevent="handleSendMessage"
    >
      <template #append>
        <!--
          The PTextInput wrapper has @click="focusInput(name)" on its root,
          so any click inside #append bubbles up and re-focuses the input.
          On mobile this causes a keyboard close→open flicker when the user
          taps these controls. Stopping propagation kills the bubble, and
          @mousedown.prevent keeps the input from losing focus on tap so
          the keyboard state doesn't change on the way to opening the
          native file picker / sending the message.
        -->
        <p-icon
          v-if="chatInput.file && !loading"
          icon="x-close-delete"
          size="xs"
          color="fg-tertiary"
          class="cursor-pointer mr-8"
          @mousedown.prevent
          @click.stop="removeFile"
        />
        <p-icon
          v-if="!loading && !disabled && !attachmentDisabled"
          class="cursor-pointer"
          icon="attachment-clip-hook"
          size="sm"
          color="fg-primary"
          @mousedown.prevent
          @click.stop="fileInputRef?.click()"
        />
        <p-loading v-else-if="loading" size="sm" />
        <button
          v-if="(chatInput.value.trim() || chatInput.file) && !loading && !disabled"
          type="button"
          class="p-file-text-input__send-btn ml-8"
          @mousedown.prevent
          @click.stop="handleSendMessage"
        >
          <p-icon icon="arrow-up" size="sm" color="fg-fix-white" />
        </button>
      </template>
    </p-text-input>
    <p
      v-if="errorMessage"
      class="p-file-text-input__error f-utility-12 t-fg-danger"
      v-text="errorMessage"
    />
    <input
      ref="fileInputRef"
      type="file"
      class="p-file-text-input__file-input"
      :accept="ACCEPT_ATTR"
      :disabled="disabled || attachmentDisabled"
      @change="handleFileChange"
    />
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const ALLOWED_EXTENSIONS = ['pdf', 'doc', 'docx', 'txt', 'csv', 'png', 'jpg', 'jpeg', 'gif', 'zip']
const ACCEPT_ATTR = ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join(',')
const MAX_SIZE_BYTES = 100 * 1024 * 1024

const props = defineProps({
  label: {
    type: String,
    default: 'general.message',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  // Hide the paperclip / file input without disabling the text field.
  // Used during subject-pick where the user can still type a message
  // (which routes through `startNewChat`) but no chatId exists yet, so
  // an attachment upload would silently no-op.
  attachmentDisabled: {
    type: Boolean,
    default: false,
  },
  // Owned by parent because the actual send/attach work is async in the
  // store; emit returns sync, so a child-local `loading` ref would flip
  // back before the upload finishes and never gate duplicate clicks.
  loading: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits<{
  send: [text: string]
  attach: [file: File]
  typing: [isTyping: boolean]
}>()

const chatInput = ref({
  value: '',
  file: null as File | null,
})

const fileInputRef = ref<HTMLInputElement | null>(null)
const errorMessage = ref('')

let errorTimeout: ReturnType<typeof setTimeout> | null = null
function setError(message: string) {
  if (errorTimeout) clearTimeout(errorTimeout)
  errorMessage.value = message
  if (!message) return
  errorTimeout = setTimeout(() => {
    errorMessage.value = ''
    errorTimeout = null
  }, 5000)
}

onUnmounted(() => {
  if (errorTimeout) clearTimeout(errorTimeout)
})

const filePlaceholder = computed(() => {
  return chatInput.value.file && !chatInput.value.value ? `${chatInput.value.file.name}` : ''
})

let typingDebounce: ReturnType<typeof setTimeout> | null = null
function emitTypingDebounced() {
  if (typingDebounce) clearTimeout(typingDebounce)
  emit('typing', true)
  typingDebounce = setTimeout(() => emit('typing', false), 1500)
}

watch(
  () => chatInput.value.value,
  (text) => {
    if (props.disabled) return
    if (text.length > 0) emitTypingDebounced()
  },
)

function validateFile(file: File): string {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? ''
  if (!ALLOWED_EXTENSIONS.includes(ext)) return t('chat.fileError.invalidType')
  if (file.size > MAX_SIZE_BYTES) return t('chat.fileError.tooLarge')
  return ''
}

const handleFileChange = (event: Event) => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  if (!file) return

  const error = validateFile(file)
  if (error) {
    setError(error)
    target.value = ''
    return
  }
  setError('')
  chatInput.value.file = file
}

const removeFile = () => {
  chatInput.value.file = null
  setError('')
  if (fileInputRef.value) fileInputRef.value.value = ''
}

const handleSendMessage = () => {
  if (props.disabled || props.loading) return

  const text = chatInput.value.value.trim()
  const file = chatInput.value.file

  if (!text && !file) return

  if (text) emit('send', text)
  if (file) emit('attach', file)

  chatInput.value.value = ''
  chatInput.value.file = null
  setError('')
  if (fileInputRef.value) fileInputRef.value.value = ''
  emit('typing', false)
}
</script>
